import { createSSEClient } from './sseClient';
import type { SSEClient, SSECallbacks } from './sseClient';
import type { FinalMessagePayload, FileInfo, WorkflowNodeEvent } from '../types/contentBlock';

/**
 * 后端聊天服务配置
 */
const BACKEND_URL = (window as any).AGIMX_CONFIG?.AI_CHAT_BACKEND_URL;

// ─── 请求/响应类型 ─────────────────────

/**
 * 发送消息请求参数
 */
export interface SendMessageRequest {
  readonly conversationId?: number;
  readonly difyCapabilityId: number;
  readonly userInput: string;
  readonly userId?: string;
  readonly extra?: Record<string, unknown>;
}

/**
 * 发送消息响应
 */
export interface SendMessageResponse {
  readonly taskId: number;
  readonly conversationId: number;
  readonly messageId: number;
}

/**
 * 流式回调接口
 *
 * ★ 重构：基于新 SSE 事件，不再有 onChunk/onContentBlock/onContentReplace
 */
export interface StreamCallbacks extends SSECallbacks {}

/**
 * 任务状态
 */
export interface TaskStatus {
  readonly taskId: number;
  readonly messageId?: number;
  readonly status: 'pending' | 'streaming' | 'completed' | 'error';
  readonly unread: boolean;
  readonly content: string;
  readonly errorMessage?: string;
}

// ─── 后端聊天服务 ─────────────────────

/**
 * 后端聊天服务
 */
export const backendChatService = {
  /**
   * 发送消息（创建任务）
   */
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const response = await fetch(`${BACKEND_URL}/api/chat/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error ?? `HTTP ${response.status}`);
    }

    return response.json();
  },

  /**
   * 建立流式连接
   */
  streamResponse(taskId: number, callbacks: StreamCallbacks): SSEClient {
    const url = `${BACKEND_URL}/api/chat/stream/${taskId}`;
    return createSSEClient({ url, ...callbacks });
  },

  /**
   * 订阅已存在的流式任务
   *
   * ★ 重构：回调接口改为 token/thinking/file/workflow_trace/done/error
   */
  subscribeToTask(params: {
    readonly taskId: number;
  } & SSECallbacks): () => void {
    const { taskId, ...callbacks } = params;
    const url = `${BACKEND_URL}/api/chat/stream/${taskId}`;
    const client = createSSEClient({ url, ...callbacks });
    return () => client.close();
  },

  /**
   * 发送消息并自动建立流式连接
   *
   * 返回 { taskId, conversationId, close }
   */
  async sendAndStream(params: {
    readonly request: SendMessageRequest;
  } & SSECallbacks): Promise<{
    taskId: number;
    conversationId: number;
    close: () => void;
  }> {
    const { request, ...callbacks } = params;

    // 1. 发送消息
    const { taskId, conversationId } = await this.sendMessage(request);

    // 2. 建立 SSE 连接
    const client = this.streamResponse(taskId, callbacks);

    return {
      taskId,
      conversationId,
      close: () => client.close()
    };
  },

  /**
   * 获取会话中进行中的流式任务
   */
  async getPendingTaskByConversation(conversationId: number): Promise<{
    taskId: number;
    messageId: number;
    status: string;
    content: string;
  } | null> {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/chat/conversation/${conversationId}/pending-task`,
        { credentials: 'include' }
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    } catch {
      return null;
    }
  },

  /**
   * 获取任务状态（通过消息 ID）
   */
  async getTaskStatusByMessageId(messageId: number): Promise<{
    taskId: number;
    status: string;
    content: string;
  } | null> {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/task/by-message/${messageId}`,
        { credentials: 'include' }
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    } catch {
      return null;
    }
  },

  /**
   * 获取任务状态
   */
  async getTaskStatus(taskId: number): Promise<{
    taskId: number;
    status: string;
    content: string;
  } | null> {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/chat/task/${taskId}`,
        { credentials: 'include' }
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    } catch {
      return null;
    }
  },

  /**
   * 标记会话的所有未读任务为已读
   */
  async markConversationAsRead(conversationId: number): Promise<void> {
    await fetch(`${BACKEND_URL}/api/chat/conversation/${conversationId}/read`, {
      method: 'POST',
      credentials: 'include'
    });
  },

  /**
   * 获取有未读消息的会话 ID 列表（支持按 businessType 过滤）
   */
  async getUnreadConversationIds(businessType?: string): Promise<readonly number[]> {
    try {
      const params = businessType ? `?businessType=${encodeURIComponent(businessType)}` : '';
      const response = await fetch(
        `${BACKEND_URL}/api/notifications/unread-conversations${params}`,
        { credentials: 'include' }
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.conversationIds ?? [];
    } catch {
      return [];
    }
  }
};